import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { CalendarDays, Link2 } from "lucide-react";
import { FormRewardSettings } from "./FormRewardSettings";
import { formApi } from "./api";
import type { FeedbackForm } from "./contract";
import { useDebounce } from "../../hooks/useDebounce";
export function FormSettings({
  form,
  onChange,
  canEvents,
  canRewards,
  locked,
}: {
  form: FeedbackForm;
  onChange: (changes: Partial<FeedbackForm>) => void;
  canEvents: boolean;
  canRewards: boolean;
  locked: boolean;
}) {
  const [search, setSearch] = useState(""),
    [events, setEvents] = useState<
      { id: string; title: string; status: string }[]
    >([]),
    [error, setError] = useState("");
  const query = useDebounce(search, 250);
  useEffect(() => {
    if (!canEvents) return;
    let stop = false;
    formApi
      .events(query)
      .then((r) => {
        if (!stop) {
          setEvents(r.events);
          setError("");
        }
      })
      .catch((e) => {
        if (!stop) setError(e.message);
      });
    return () => {
      stop = true;
    };
  }, [query, canEvents]);
  return (
    <div className="space-y-4" data-tour="forms-settings">
      <section className="glass-card p-5 space-y-4">
        <h2 className="font-medium text-sm">A thoughtful finish</h2>
        <label className="block text-xs space-y-2">
          <span className="text-glass-400">Thank-you message</span>
          <textarea
            className="glass-input w-full"
            rows={4}
            maxLength={1000}
            value={form.confirmation}
            onChange={(e) => onChange({ confirmation: e.target.value })}
          />
        </label>
        <label className="block text-xs space-y-2">
          <span className="text-glass-400">
            Close automatically{" "}
            <span className="text-glass-500">(optional · your local time)</span>
          </span>
          <input
            className="glass-input w-full"
            type="datetime-local"
            value={
              form.closes_at
                ? new Date(
                    form.closes_at -
                      new Date(form.closes_at).getTimezoneOffset() * 60000,
                  )
                    .toISOString()
                    .slice(0, 16)
                : ""
            }
            onChange={(e) =>
              onChange({
                closes_at: e.target.value
                  ? new Date(e.target.value).getTime()
                  : null,
              })
            }
          />
        </label>
        <div className="subcard p-3 text-xs text-glass-400 leading-relaxed">
          Only signed-in members with a linked Minecraft account can respond.
          One response per person. Forms are excluded from search engines. Staff
          can see who submitted each response.
        </div>
      </section>
      <section className="glass-card p-5 space-y-4">
        <div className="flex gap-3 items-start">
          <Link2 className="w-5 h-5 text-accent-300 shrink-0" />
          <div>
            <h2 className="font-medium text-sm">Connect an event</h2>
            <p className="text-xs text-glass-400 mt-1">
              Optional. Keep feedback beside the event that inspired it.
            </p>
          </div>
        </div>
        {locked && (
          <p className="text-xs text-glass-400">
            Event settings are locked after the first response.
          </p>
        )}
        {form.event_id && (
          <Link
            className="text-xs text-accent-300 block"
            to={`/event-team/events/${form.event_id}`}
          >
            Open {form.event_title || "linked event"} →
          </Link>
        )}
        {error && (
          <p role="alert" className="text-xs text-red-400">
            {error}
          </p>
        )}
        <fieldset
          disabled={locked || !canEvents}
          className="space-y-3 disabled:opacity-60"
        >
          <input
            aria-label="Search events to link"
            className="glass-input w-full"
            placeholder="Search events…"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
          />
          <select
            className="glass-select w-full"
            aria-label="Linked event"
            value={form.event_id || ""}
            onChange={(e) => {
              const event = events.find((x) => x.id === e.target.value);
              onChange({
                event_id: e.target.value || null,
                event_title: event?.title || null,
                event_status: event?.status || null,
                after_event: !!e.target.value,
                award_points: false,
                ...(form.reward_kind === "event_point"
                  ? { reward_kind: "none", reward_id: null, reward_label: "" }
                  : {}),
              });
            }}
          >
            <option value="">No event · standalone form</option>
            {form.event_id && !events.some((e) => e.id === form.event_id) && (
              <option value={form.event_id}>{form.event_title}</option>
            )}
            {events.map((e) => (
              <option key={e.id} value={e.id}>
                {e.title} · {e.status.replace(/_/g, " ")}
              </option>
            ))}
          </select>
          {form.event_id && (
            <>
              <label className="subcard p-3 flex gap-3 text-xs items-start">
                <input
                  type="checkbox"
                  checked={form.after_event}
                  onChange={(e) =>
                    onChange({
                      after_event: e.target.checked,
                      ...(!e.target.checked &&
                      form.reward_kind === "event_point"
                        ? {
                            reward_kind: "none",
                            reward_id: null,
                            reward_label: "",
                          }
                        : {}),
                      award_points: e.target.checked
                        ? form.award_points
                        : false,
                    })
                  }
                />
                <CalendarDays className="w-4 h-4 shrink-0" />
                <span>
                  <strong className="block font-medium">
                    Open after the event finishes
                  </strong>
                  <span className="text-glass-400 block mt-1">
                    Accept answers when the event is marked completed in Argus.
                  </span>
                </span>
              </label>
            </>
          )}
        </fieldset>
        {!canEvents && (
          <p className="text-xs text-glass-400">
            Event edit access is required to change this integration.
          </p>
        )}
      </section>
      <FormRewardSettings
        form={form}
        onChange={onChange}
        canRewards={canRewards}
        canEvents={canEvents}
        locked={locked}
      />
    </div>
  );
}
